import React, { useState, useEffect } from 'react';
import { Edge, MarkerType } from 'reactflow';
import { FiX, FiArrowRight, FiArrowLeft } from 'react-icons/fi';

interface EdgeSettingsPanelProps {
  edge: Edge | null;
  onClose: () => void;
  onUpdate: (edgeId: string, updates: Partial<Edge>) => void;
}

interface EdgeType {
  value: string;
  label: string;
  icon: string;
  description: string;
  color: string;
  dasharray?: string;
  width: number;
  animated?: boolean;
}

interface EdgeDirection {
  value: string;
  label: string;
  symbol: string;
  icon: React.ReactNode;
  description: string;
}

const EDGE_TYPES: EdgeType[] = [
  {
    value: 'property_reference',
    label: 'Property Reference',
    icon: '→',
    description: 'Référence via propriété (ex: subnet_id → subnet)',
    color: '#2196f3',
    width: 2,
  },
  {
    value: 'explicit_depends_on',
    label: 'Explicit Depends On',
    icon: '⇢',
    description: 'Dépendance explicite Terraform (depends_on)',
    color: '#9c27b0',
    dasharray: '5,5',
    width: 2,
  },
  {
    value: 'network_connection',
    label: 'Network Connection',
    icon: '↔',
    description: 'Connexion réseau mutuelle',
    color: '#ff9800',
    width: 2,
  },
  {
    value: 'data_flow',
    label: 'Data Flow',
    icon: '⇒',
    description: 'Flux de données entre ressources',
    color: '#4caf50',
    width: 3,
    animated: true,
  },
];

const EDGE_DIRECTIONS: EdgeDirection[] = [
  {
    value: 'unidirectional',
    label: 'Unidirectionnel',
    symbol: '→',
    icon: <FiArrowRight />,
    description: 'Source dépend de Target',
  },
  {
    value: 'bidirectional',
    label: 'Bidirectionnel',
    symbol: '↔',
    icon: <><FiArrowRight /><FiArrowLeft /></>,
    description: 'Connexion mutuelle',
  },
  {
    value: 'reversed',
    label: 'Inversé',
    symbol: '←',
    icon: <FiArrowLeft />,
    description: 'Target dépend de Source',
  },
];

const EdgeSettingsPanel: React.FC<EdgeSettingsPanelProps> = ({ edge, onClose, onUpdate }) => {
  const [selectedType, setSelectedType] = useState<string>('property_reference');
  const [selectedDirection, setSelectedDirection] = useState<string>('unidirectional');
  const [label, setLabel] = useState<string>('');

  useEffect(() => {
    if (edge) {
      setSelectedType(edge.data?.dependencyType || 'property_reference');
      setSelectedDirection(edge.data?.direction || 'unidirectional');
      setLabel(edge.label as string || '');
    }
  }, [edge]);

  if (!edge) return null;

  const handleApply = () => {
    const selectedTypeConfig = EDGE_TYPES.find(t => t.value === selectedType);
    if (!selectedTypeConfig) return;

    const updates: Partial<Edge> = {
      type: selectedType === 'network_connection' ? 'straight' : selectedType === 'explicit_depends_on' ? 'step' : 'smoothstep',
      animated: selectedTypeConfig.animated || false,
      label: label || selectedTypeConfig.label,
      style: {
        stroke: selectedTypeConfig.color,
        strokeWidth: selectedTypeConfig.width,
        strokeDasharray: selectedTypeConfig.dasharray,
      },
      markerEnd: selectedDirection === 'reversed' ? undefined : {
        type: MarkerType.ArrowClosed,
        color: selectedTypeConfig.color,
      },
      markerStart: selectedDirection === 'reversed' || selectedDirection === 'bidirectional' ? {
        type: MarkerType.ArrowClosed,
        color: selectedTypeConfig.color,
      } : undefined,
      data: {
        ...edge.data,
        dependencyType: selectedType,
        direction: selectedDirection,
      },
    };

    onUpdate(edge.id, updates);
    onClose();
  };

  const selectedTypeConfig = EDGE_TYPES.find(t => t.value === selectedType);
  const selectedDirectionConfig = EDGE_DIRECTIONS.find(d => d.value === selectedDirection);

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 420,
        maxHeight: '80vh',
        background: 'white',
        borderRadius: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f9fafb',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>
          🔗 Configuration du lien
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            color: '#6b7280',
          }}
        >
          <FiX size={20} />
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
        {/* Label */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            Label du lien
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ex: depends on, linked to, sends data to..."
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* Type de lien */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            Type de lien
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {EDGE_TYPES.map((type) => (
              <div
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                style={{
                  padding: 12,
                  border: `2px solid ${selectedType === type.value ? type.color : '#e5e7eb'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: selectedType === type.value ? `${type.color}10` : 'white',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 20, color: type.color }}>{type.icon}</span>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{type.label}</span>
                  {selectedType === type.value && (
                    <span style={{
                      marginLeft: 'auto',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: type.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 10,
                    }}>✓</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginLeft: 30 }}>
                  {type.description}
                </div>
                {/* Preview line */}
                <div style={{ marginTop: 8, marginLeft: 30 }}>
                  <svg width="140" height="20">
                    <line
                      x1="10"
                      y1="10"
                      x2="130"
                      y2="10"
                      stroke={type.color}
                      strokeWidth={type.width}
                      strokeDasharray={type.dasharray}
                    />
                    <polygon
                      points="130,10 125,7 125,13"
                      fill={type.color}
                    />
                    {type.animated && (
                      <circle cx="70" cy="10" r="3" fill={type.color}>
                        <animate attributeName="cx" from="10" to="130" dur="2s" repeatCount="indefinite" />
                      </circle>
                    )}
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Direction */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            Direction
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {EDGE_DIRECTIONS.map((direction) => (
              <div
                key={direction.value}
                onClick={() => setSelectedDirection(direction.value)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: `2px solid ${selectedDirection === direction.value ? selectedTypeConfig?.color || '#2196f3' : '#e5e7eb'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: selectedDirection === direction.value ? `${selectedTypeConfig?.color || '#2196f3'}10` : 'white',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ fontSize: 18, marginBottom: 4 }}>{direction.symbol}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{direction.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div
          style={{
            padding: 16,
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px solid #e5e7eb',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: '#6b7280' }}>
            Aperçu du rendu
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
            <div
              style={{
                padding: '8px 16px',
                background: '#3B82F6',
                color: 'white',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              Source
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg width="100%" height="30">
                <defs>
                  <marker
                    id="preview-arrow-end"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="0,0 0,6 9,3" fill={selectedTypeConfig?.color || '#2196f3'} />
                  </marker>
                  <marker
                    id="preview-arrow-start"
                    markerWidth="10"
                    markerHeight="10"
                    refX="1"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="9,0 9,6 0,3" fill={selectedTypeConfig?.color || '#2196f3'} />
                  </marker>
                </defs>
                <line
                  x1="5"
                  y1="15"
                  x2="95%"
                  y2="15"
                  stroke={selectedTypeConfig?.color || '#2196f3'}
                  strokeWidth={selectedTypeConfig?.width || 2}
                  strokeDasharray={selectedTypeConfig?.dasharray}
                  markerEnd={selectedDirection !== 'reversed' ? 'url(#preview-arrow-end)' : undefined}
                  markerStart={selectedDirection === 'reversed' || selectedDirection === 'bidirectional' ? 'url(#preview-arrow-start)' : undefined}
                />
              </svg>
              {label && (
                <div
                  style={{
                    position: 'absolute',
                    top: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'white',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 10,
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
              )}
            </div>
            <div
              style={{
                padding: '8px 16px',
                background: '#10B981',
                color: 'white',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              Target
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: '#6b7280', textAlign: 'center' }}>
            {selectedDirectionConfig?.description}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 20px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          background: '#f9fafb',
        }}
      >
        <button
          onClick={onClose}
          style={{
            padding: '8px 16px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            background: 'white',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            color: '#374151',
          }}
        >
          Annuler
        </button>
        <button
          onClick={handleApply}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: 6,
            background: selectedTypeConfig?.color || '#2196f3',
            color: 'white',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Appliquer
        </button>
      </div>
    </div>
  );
};

export default EdgeSettingsPanel;
